import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Plus, MessageSquare, Clock, Trash2, CheckCircle2, ListChecks } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["open", "in_progress", "review", "done", "blocked"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const priColors = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high:   "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low:    "bg-slate-100 text-slate-700 border-slate-200",
};

const statusColors = {
  open: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  review: "bg-purple-100 text-purple-700",
  done: "bg-emerald-100 text-emerald-700",
  blocked: "bg-red-100 text-red-700",
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [scope, setScope] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [view, setView] = useState("kanban");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = { scope };
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get("/tasks", { params });
    setTasks(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, statusFilter]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => {});
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => {});
  }, []);

  const updateStatus = async (t, status) => {
    await api.patch(`/tasks/${t.id}`, { status });
    load();
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this task and its sub-tasks?")) return;
    await api.delete(`/tasks/${id}`);
    setSelected(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Office Admin</div>
          <h1 className="font-sans text-3xl">Task Manager</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-32" data-testid="task-scope-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="mine">My Tasks</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="w-36" data-testid="task-status-filter"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setOpen(true)} data-testid="task-new-btn"><Plus className="h-4 w-4 mr-1"/> New Task</Button>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="task-view-tabs">
          <TabsTrigger value="kanban" data-testid="task-tab-kanban">Kanban</TabsTrigger>
          <TabsTrigger value="list" data-testid="task-tab-list">List</TabsTrigger>
        </TabsList>
        <TabsContent value="kanban">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
            {STATUSES.map((s) => (
              <div key={s} className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`kanban-col-${s}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">{s.replace("_", " ")}</div>
                  <Badge variant="outline">{tasks.filter((t) => t.status === s).length}</Badge>
                </div>
                <div className="space-y-2 min-h-[100px]">
                  {tasks.filter((t) => t.status === s).map((t) => (
                    <div key={t.id} onClick={() => setSelected(t)}
                      className="bg-slate-50 border border-slate-200 rounded-sm p-3 cursor-pointer hover:border-slate-400 transition-colors"
                      data-testid={`task-card-${t.id}`}>
                      <div className="text-sm font-medium text-slate-900 line-clamp-2">{t.title}</div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className={priColors[t.priority]}>{t.priority}</Badge>
                        {t.due_date && <span className="text-[11px] text-slate-500">{t.due_date}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="list">
          <Card className="p-0 mt-4 overflow-hidden">
            <table className="w-full text-sm" data-testid="task-list-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Priority</th>
                  <th className="text-left p-3">Due</th>
                  <th className="text-left p-3">Assignee</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(t)}>
                    <td className="p-3 font-medium text-slate-800">{t.title}</td>
                    <td className="p-3"><Badge className={statusColors[t.status]}>{t.status}</Badge></td>
                    <td className="p-3"><Badge className={priColors[t.priority]}>{t.priority}</Badge></td>
                    <td className="p-3 text-slate-600">{t.due_date || "—"}</td>
                    <td className="p-3 text-slate-600">{users.find(u => u.id === t.assignee_id)?.name || "Unassigned"}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(t.id); }}><Trash2 className="h-3 w-3"/></Button>
                    </td>
                  </tr>
                ))}
                {!tasks.length && <tr><td colSpan="6" className="text-center text-slate-400 p-8">No tasks yet</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <TaskFormDialog open={open} onClose={() => setOpen(false)} users={users} clients={clients} projects={projects} onSaved={load}/>
      {selected && (
        <TaskDetailDialog
          task={selected} onClose={() => setSelected(null)}
          users={users} clients={clients} projects={projects}
          onSaved={() => { setSelected(null); load(); }}
          onDelete={onDelete} onStatusChange={updateStatus}
        />
      )}
    </div>
  );
}

function TaskFormDialog({ open, onClose, users, clients, projects, onSaved, parent }) {
  const [form, setForm] = useState({
    title: "", description: "", assignee_id: "", due_date: "", priority: "medium",
    status: "open", client_id: "", project_id: "", tags: "", parent_task_id: parent?.id || "",
    recurring: "", estimated_hours: "",
  });
  useEffect(() => { setForm((f) => ({ ...f, parent_task_id: parent?.id || "" })); }, [parent]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      await api.post("/tasks", {
        ...form,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        due_date: form.due_date || null,
        assignee_id: form.assignee_id || null,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        parent_task_id: form.parent_task_id || null,
        recurring: form.recurring || null,
        estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
      });
      toast.success("Task created");
      onClose();
      onSaved?.();
      setForm({ title: "", description: "", assignee_id: "", due_date: "", priority: "medium", status: "open", client_id: "", project_id: "", tags: "", parent_task_id: parent?.id || "", recurring: "", estimated_hours: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="task-form-dialog">
        <DialogHeader><DialogTitle>{parent ? `New Sub-task of "${parent.title}"` : "New Task"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Title *</label>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="task-title-input"/>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Description</label>
            <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3}/>
          </div>
          <div>
            <label className="text-xs text-slate-500">Assignee</label>
            <Select value={form.assignee_id || "_none"} onValueChange={(v) => setForm({...form, assignee_id: v === "_none" ? "" : v})}>
              <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Pick user"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Unassigned</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Due Date</label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})}/>
          </div>
          <div>
            <label className="text-xs text-slate-500">Priority</label>
            <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Client</label>
            <Select value={form.client_id || "_none"} onValueChange={(v) => setForm({...form, client_id: v === "_none" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="(optional)"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Project</label>
            <Select value={form.project_id || "_none"} onValueChange={(v) => setForm({...form, project_id: v === "_none" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="(optional)"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Recurring</label>
            <Select value={form.recurring || "_none"} onValueChange={(v) => setForm({...form, recurring: v === "_none" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="(none)"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Estimated Hours</label>
            <Input type="number" step="0.5" value={form.estimated_hours} onChange={(e) => setForm({...form, estimated_hours: e.target.value})}/>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Tags (comma-separated)</label>
            <Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="urgent, audit-2024, ABC-Ltd"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="task-save-btn">Save Task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskDetailDialog({ task, onClose, users, clients, projects, onSaved, onDelete, onStatusChange }) {
  const [t, setT] = useState(task);
  const [subTasks, setSubTasks] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [logHours, setLogHours] = useState("");
  const [logNotes, setLogNotes] = useState("");
  const [subOpen, setSubOpen] = useState(false);

  useEffect(() => { setT(task); }, [task]);
  useEffect(() => {
    api.get("/tasks", { params: { parent_task_id: task.id } })
      .then(({ data }) => setSubTasks(data || []))
      .catch(() => {});
  }, [task.id]);

  const update = async (changes) => {
    const { data } = await api.patch(`/tasks/${t.id}`, changes);
    setT(data);
    onSaved?.();
  };
  const addComment = async () => {
    if (!commentText.trim()) return;
    await api.post(`/tasks/${t.id}/comment`, { text: commentText });
    setCommentText("");
    const { data } = await api.patch(`/tasks/${t.id}`, {});
    setT(data);
  };
  const addTime = async () => {
    const hrs = parseFloat(logHours);
    if (!hrs || hrs <= 0) { toast.error("Enter hours"); return; }
    await api.post(`/tasks/${t.id}/time`, { hours: hrs, notes: logNotes });
    setLogHours(""); setLogNotes("");
    const { data } = await api.patch(`/tasks/${t.id}`, {});
    setT(data);
    toast.success("Time logged");
  };
  const totalLogged = (t.time_entries || []).reduce((s, x) => s + (x.hours || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5"/>{t.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <label className="text-xs text-slate-500">Status</label>
              <Select value={t.status} onValueChange={(v) => update({ status: v })}>
                <SelectTrigger data-testid="detail-status"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Priority</label>
              <Select value={t.priority} onValueChange={(v) => update({ priority: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Assignee</label>
              <Select value={t.assignee_id || "_none"} onValueChange={(v) => update({ assignee_id: v === "_none" ? null : v })}>
                <SelectTrigger><SelectValue placeholder="Unassigned"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {t.description && <div><label className="text-xs text-slate-500">Description</label><p className="text-sm text-slate-700 whitespace-pre-wrap">{t.description}</p></div>}
          <div className="text-xs text-slate-500 flex gap-4 flex-wrap">
            {t.due_date && <span>Due: {t.due_date}</span>}
            {t.client_id && <span>Client: {clients.find(c => c.id === t.client_id)?.name}</span>}
            {t.project_id && <span>Project: {projects.find(p => p.id === t.project_id)?.name}</span>}
            {t.recurring && <span>Recurring: {t.recurring}</span>}
            {t.estimated_hours != null && <span>Estimated: {t.estimated_hours}h</span>}
            <span>Logged: {totalLogged.toFixed(1)}h</span>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold flex items-center gap-1"><CheckCircle2 className="h-4 w-4"/> Sub-tasks ({subTasks.length})</div>
              <Button size="sm" variant="outline" onClick={() => setSubOpen(true)} data-testid="add-subtask-btn">Add Sub-task</Button>
            </div>
            <div className="space-y-1">
              {subTasks.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm border border-slate-100 rounded p-2">
                  <span className="text-slate-800">{s.title}</span>
                  <Badge className={statusColors[s.status]}>{s.status}</Badge>
                </div>
              ))}
              {!subTasks.length && <div className="text-xs text-slate-400">No sub-tasks</div>}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <div className="text-sm font-semibold mb-2 flex items-center gap-1"><MessageSquare className="h-4 w-4"/> Comments ({(t.comments || []).length})</div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(t.comments || []).map((c) => (
                <div key={c.id} className="bg-slate-50 rounded p-2 text-sm">
                  <div className="flex justify-between text-xs text-slate-500"><span>{c.by_name}</span><span>{c.at?.slice(0, 16).replace("T", " ")}</span></div>
                  <div>{c.text}</div>
                </div>
              ))}
              {!(t.comments || []).length && <div className="text-xs text-slate-400">No comments</div>}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add a comment…" data-testid="comment-input"/>
              <Button onClick={addComment} data-testid="comment-submit">Post</Button>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <div className="text-sm font-semibold mb-2 flex items-center gap-1"><Clock className="h-4 w-4"/> Time Log</div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {(t.time_entries || []).map((te) => (
                <div key={te.id} className="text-sm flex justify-between">
                  <span>{te.by_name} · {te.notes || "—"}</span>
                  <span className="font-mono">{te.hours.toFixed(1)}h</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input type="number" step="0.25" value={logHours} onChange={(e) => setLogHours(e.target.value)} placeholder="Hrs" className="w-24" data-testid="time-hours"/>
              <Input value={logNotes} onChange={(e) => setLogNotes(e.target.value)} placeholder="Notes (optional)"/>
              <Button onClick={addTime} data-testid="time-submit">Log</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="destructive" onClick={() => onDelete(t.id)} data-testid="task-delete-btn"><Trash2 className="h-4 w-4 mr-1"/>Delete</Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
        <TaskFormDialog open={subOpen} onClose={() => setSubOpen(false)} users={users} clients={clients} projects={projects} parent={t} onSaved={() => { setSubOpen(false); api.get("/tasks", { params: { parent_task_id: t.id } }).then(({ data }) => setSubTasks(data || [])); }}/>
      </DialogContent>
    </Dialog>
  );
}
